import { useHealthCheck as useHealthCheckQuery } from '@/api/generated/admin/admin';
import { useEffect, useState } from 'react';

export interface HealthStatus {
  isHealthy: boolean;
  isLoading: boolean;
  lastChecked: Date | null;
  error: Error | null;
  data: {
    status: string;
    uptimeSeconds: number;
    databaseConnected: boolean;
  } | null;
}

export const useHealthCheck = (intervalMs: number = 30000) => {
  const [healthStatus, setHealthStatus] = useState<HealthStatus>({
    isHealthy: false,
    isLoading: true,
    lastChecked: null,
    error: null,
    data: null,
  });

  const { data, error, isLoading, refetch, isError } = useHealthCheckQuery({
    query: {
      refetchInterval: intervalMs,
      retry: 2,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      staleTime: 0, // Always consider health data stale to ensure fresh checks
      gcTime: 0, // Don't cache health check results
    },
  });

  useEffect(() => {
    const isHealthy = !isError && !error && data?.status === 'UP' && data?.databaseConnected;

    setHealthStatus({
      isHealthy,
      isLoading,
      lastChecked: new Date(),
      error: error as Error | null,
      data: data || null,
    });
  }, [data, error, isError, isLoading]);

  // Manual health check trigger
  const checkHealth = () => {
    refetch();
  };

  return {
    ...healthStatus,
    checkHealth,
  };
};
